/**
 * Contracts for module "opportunities". Owner: crm. Mounted at /v1/opportunities (CRM_DB). Flag: crm.opportunities.
 *
 * Customer <-> account manager flow for a Quixo Marketing opportunity:
 *   1. requirement gathering (customer, draft) -> final submit: email to the customer and to Quixo staff
 *   2. Quixo staff send a quotation (services and modules, each with a list price)
 *   3. the customer chooses services/modules and accepts, rejects, or negotiates (budget + required final price)
 *   4. the account manager revises the price. A discount above the manager's own limit needs approval:
 *      first the account manager's manager, then final approval from the head of operations
 *   5. staff send the updated, approved pricing back; the customer accepts / rejects / adjusts again
 *   6. repeat until either side accepts or rejects
 * Money is integer minor units; discounts are basis points (1000 = 10%). Prices are computed on the server.
 *
 * Sellers and buyers (migration 0151): every opportunity has a seller (Quixo, or a partner agency selling to its own
 * client workspaces) and a buyer (a customer workspace, or an agency buying from Quixo). Each seller runs its own desk:
 * sales staff, reporting line, self-approval limits and one head of operations.
 */
import { z } from 'zod';
import { INTAKE_SERVICES } from './smart-intake';

export const OPPORTUNITIES_FLAG = 'crm.opportunities';

/** seller_key of Quixo's own desk; an agency's desk uses its partner id. */
export const SELLER_QUIXO = 'quixo';
export const SELLER_QUIXO_NAME = 'Quixo Marketing';
/** 'tenant': a customer workspace buys (tenant_id is the workspace). 'partner': an agency buys from Quixo (tenant_id holds the agency partner id). */
export const BUYER_KINDS = ['tenant', 'partner'] as const;
export type BuyerKind = (typeof BUYER_KINDS)[number];

/** Services a customer can ask for (the smart-intake service list, so both flows speak the same words). */
export const OPPORTUNITY_SERVICES = INTAKE_SERVICES;
/** Quixo OS platform modules a customer can ask for. */
export const OPPORTUNITY_MODULES = [
  'growth_audit', 'crm', 'whatsapp_inbox', 'whatsapp_campaigns', 'ads_manager', 'content_studio',
  'social_scheduler', 'reports', 'ai_assistant', 'knowledge_base', 'visibility', 'white_label',
] as const;
export const OPPORTUNITY_CURRENCIES = ['AED', 'USD'] as const;
export const OPPORTUNITY_TIMELINES = ['asap', 'within_1_month', 'within_3_months', 'flexible'] as const;

export const OPPORTUNITY_STATUSES = [
  'draft', // customer is still gathering requirements
  'submitted', // requirements submitted, waiting for the first quotation
  'quoted', // a quotation (or approved revision) is with the customer
  'negotiating', // the customer asked for a different price; with the account manager
  'pending_approval', // a revision with a high discount is waiting for the manager / head of operations
  'approved', // the revision is approved and ready for staff to send
  'accepted', // closed: won
  'rejected', // closed: lost (rejected by the customer or by Quixo)
] as const;
export type OpportunityStatus = (typeof OPPORTUNITY_STATUSES)[number];
export const CLOSED_STATUSES: readonly OpportunityStatus[] = ['accepted', 'rejected'];

export const VERSION_STATUSES = ['pending_approval', 'approved', 'sent', 'superseded', 'declined_internally'] as const;
export type VersionStatus = (typeof VERSION_STATUSES)[number];
export const APPROVAL_STAGES = ['manager', 'head_of_operations'] as const;
export type ApprovalStage = (typeof APPROVAL_STAGES)[number];

/** An account manager may send up to this discount without approval unless their profile says otherwise. */
export const DEFAULT_SELF_APPROVAL_BPS = 1000;

const Money = z.number().int().min(0).max(1_000_000_000_000);
const Text = (max: number) => z.string().trim().max(max);
const Id = z.string().min(1).max(64);

// ---------- requirements (customer) ----------
export const RequirementsSchema = z.object({
  title: Text(200).min(3),
  services: z.array(z.enum(OPPORTUNITY_SERVICES)).max(OPPORTUNITY_SERVICES.length).default([]),
  modules: z.array(z.enum(OPPORTUNITY_MODULES)).max(OPPORTUNITY_MODULES.length).default([]),
  description: Text(4000).default(''),
  goals: Text(2000).default(''),
  timeline: z.enum(OPPORTUNITY_TIMELINES).optional(),
  currency: z.enum(OPPORTUNITY_CURRENCIES).default('AED'),
  /** optional indicative budget the customer is comfortable sharing up front */
  budgetMinor: Money.optional(),
  contactName: Text(200).optional(),
  contactPhone: Text(40).optional(),
}).strict();
export type Requirements = z.infer<typeof RequirementsSchema>;

/** Creating an opportunity: the requirements plus, for a customer, an optional agency to send them to instead of Quixo. */
export const CreateOpportunitySchema = RequirementsSchema.extend({ sellerPartnerId: z.string().trim().min(1).max(64).optional() }).strict();
export type CreateOpportunityInput = z.infer<typeof CreateOpportunitySchema>;

/** Who a customer can send requirements to: Quixo, and each agency with an active client grant. */
export interface SellerOption { kind: 'quixo' | 'agency'; /** null for Quixo */ partnerId: string | null; name: string }
export interface SellerView { kind: 'quixo' | 'agency'; name: string }

/** Final submit needs at least one service or module and a description of the need. */
export function submitProblems(r: Requirements): string[] {
  const out: string[] = [];
  if (!r.services.length && !r.modules.length) out.push('services_or_modules');
  if (r.description.trim().length < 20) out.push('description');
  return out;
}

// ---------- quotation (staff) ----------
export const QuotationItemSchema = z.object({
  kind: z.enum(['service', 'module']),
  /** a value from OPPORTUNITY_SERVICES / OPPORTUNITY_MODULES, or 'custom' with a name */
  code: Text(60).min(1),
  name: Text(200).min(1),
  description: Text(1000).default(''),
  listPriceMinor: Money,
  /** a required item is always part of the deal; the customer may not deselect it */
  required: z.boolean().default(false),
}).strict();
export type QuotationItemInput = z.infer<typeof QuotationItemSchema>;
export interface QuotationItem extends QuotationItemInput { id: string }

export const QuotationInputSchema = z.object({
  items: z.array(QuotationItemSchema).min(1).max(60),
  /** discount on every item, basis points. Mutually exclusive with offeredTotalMinor. */
  discountBps: z.number().int().min(0).max(10_000).optional(),
  /** a target price for `basisItemIds` (default: every item); the discount is derived from it */
  offeredTotalMinor: Money.optional(),
  basisItemIds: z.array(Id).max(60).optional(),
  message: Text(2000).default(''),
  validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
}).strict().refine((v) => v.discountBps === undefined || v.offeredTotalMinor === undefined, { message: 'Give either discountBps or offeredTotalMinor, not both' });
export type QuotationInput = z.infer<typeof QuotationInputSchema>;

/** A revision keeps the items of the version it revises and changes the price. */
export const RevisionInputSchema = z.object({
  discountBps: z.number().int().min(0).max(10_000).optional(),
  offeredTotalMinor: Money.optional(),
  /** default: the customer's latest selection */
  basisItemIds: z.array(Id).max(60).optional(),
  message: Text(2000).default(''),
  validUntil: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
}).strict().refine((v) => (v.discountBps === undefined) !== (v.offeredTotalMinor === undefined), { message: 'Give exactly one of discountBps or offeredTotalMinor' });
export type RevisionInput = z.infer<typeof RevisionInputSchema>;

// ---------- customer response ----------
export const CustomerResponseSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('accept'), versionId: Id, selectedItemIds: z.array(Id).min(1).max(60) }).strict(),
  z.object({ action: z.literal('reject'), versionId: Id, reason: Text(1000).min(3) }).strict(),
  z.object({
    action: z.literal('counter'),
    versionId: Id,
    selectedItemIds: z.array(Id).min(1).max(60),
    /** the customer's overall budget (optional context for the account manager) */
    budgetMinor: Money.optional(),
    /** the final price the customer requires for the selected items */
    requiredPriceMinor: Money,
    note: Text(2000).default(''),
  }).strict(),
]);
export type CustomerResponse = z.infer<typeof CustomerResponseSchema>;

// ---------- staff ----------
export const AssignInputSchema = z.object({ accountManagerId: Id }).strict();
export const ApprovalDecisionSchema = z.object({ decision: z.enum(['approve', 'reject']), note: Text(1000).default('') }).strict()
  .refine((v) => v.decision === 'approve' || v.note.length >= 3, { message: 'Say why the price is rejected', path: ['note'] });
export const CloseInputSchema = z.object({ reason: Text(1000).min(3) }).strict();

export const SalesStaffInputSchema = z.object({
  name: Text(200).min(1),
  email: z.string().trim().toLowerCase().email().max(254),
  managerId: Id.nullable().default(null),
  headOfOperations: z.boolean().default(false),
  /** the largest discount this person may send without approval */
  selfApprovalBps: z.number().int().min(0).max(10_000).default(DEFAULT_SELF_APPROVAL_BPS),
  active: z.boolean().default(true),
}).strict();
export type SalesStaffInput = z.infer<typeof SalesStaffInputSchema>;

export const ListQuerySchema = z.object({
  status: z.enum(OPPORTUNITY_STATUSES).optional(),
  /** staff only: 'mine' = assigned to me, 'approvals' = waiting for my approval */
  view: z.enum(['all', 'mine', 'approvals']).default('all'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});

// ---------- pricing ----------
/** Price after a discount, rounded half up to the minor unit. */
export const discounted = (listMinor: number, discountBps: number): number => Math.round((listMinor * (10_000 - discountBps)) / 10_000);

/** Discount (bps, rounded up so an approval threshold is never under-counted) that turns list into offered. */
export function discountFor(listMinor: number, offeredMinor: number): number {
  if (listMinor <= 0 || offeredMinor >= listMinor) return 0;
  return Math.min(10_000, Math.ceil(((listMinor - offeredMinor) * 10_000) / listMinor));
}

export function selectionTotal(items: readonly Pick<QuotationItem, 'id' | 'listPriceMinor'>[], selected: readonly string[]): number {
  const set = new Set(selected);
  return items.filter((i) => set.has(i.id)).reduce((s, i) => s + i.listPriceMinor, 0);
}

/** Price of a selection on a version: the exact offered total for its basis selection, else the version discount. */
export function priceForSelection(v: { items: readonly Pick<QuotationItem, 'id' | 'listPriceMinor'>[]; basisItemIds: readonly string[]; offeredTotalMinor: number; discountBps: number }, selection: readonly string[]): number {
  const same = v.basisItemIds.length === selection.length && v.basisItemIds.every((id) => selection.includes(id));
  return same ? v.offeredTotalMinor : discounted(selectionTotal(v.items, selection), v.discountBps);
}

/**
 * Who must approve a discount. Up to the account manager's own limit: nobody. Above it: the account manager's
 * manager, then the head of operations for final approval. A stage is skipped when that person is the account
 * manager or already approves at an earlier stage (one person never approves twice).
 */
export function approvalChain(input: { discountBps: number; selfApprovalBps: number; accountManagerId: string; managerId: string | null; headOfOperationsId: string | null }):
  { stage: ApprovalStage; approverId: string }[] {
  if (input.discountBps <= input.selfApprovalBps) return [];
  const chain: { stage: ApprovalStage; approverId: string }[] = [];
  if (input.managerId && input.managerId !== input.accountManagerId && input.managerId !== input.headOfOperationsId) chain.push({ stage: 'manager', approverId: input.managerId });
  if (input.headOfOperationsId && input.headOfOperationsId !== input.accountManagerId) chain.push({ stage: 'head_of_operations', approverId: input.headOfOperationsId });
  return chain;
}

// ---------- views ----------
export interface VersionView {
  id: string;
  version: number;
  status: VersionStatus;
  items: QuotationItem[];
  discountBps: number;
  basisItemIds: string[];
  listTotalMinor: number;
  offeredTotalMinor: number;
  /** this revision accepts the customer's counter: once approved the deal is won without another customer step */
  acceptsCounter: boolean;
  message: string;
  validUntil: string | null;
  createdBy: string;
  createdAt: string;
  sentAt: string | null;
  approvals?: { stage: ApprovalStage; approverId: string; decision: 'pending' | 'approved' | 'rejected'; note: string; decidedAt: string | null }[];
}

export interface EventView { id: string; kind: string; actorType: 'customer' | 'staff' | 'system'; actorId: string | null; detail: Record<string, unknown>; createdAt: string }

export interface OpportunityView {
  id: string;
  number: string;
  /** staff only: the buying workspace (a tenant id, or the agency partner id when buyerKind is 'partner') */
  tenantId?: string;
  /** staff only: the buyer's display name (workspace name, or the agency's company name) */
  buyerName?: string;
  seller: SellerView;
  buyerKind: BuyerKind;
  status: OpportunityStatus;
  requirements: Requirements;
  currency: string;
  accountManagerId: string | null;
  accountManagerName: string | null;
  /** the customer's latest selection / counter */
  selection: string[];
  customerBudgetMinor: number | null;
  customerRequiredPriceMinor: number | null;
  customerNote: string | null;
  finalTotalMinor: number | null;
  closeReason: string | null;
  submittedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** customers see only versions that were sent to them; staff see every version */
  versions: VersionView[];
  events: EventView[];
}

// ---------- email (bridged job) ----------
export const OPPORTUNITY_EMAIL_JOB = 'opportunities.email.send';
export const OPPORTUNITY_EMAIL_EVENTS = [
  'submitted', 'quotation_sent', 'customer_accepted', 'customer_rejected', 'customer_countered',
  'approval_requested', 'approval_rejected', 'revision_approved', 'closed_by_quixo', 'counter_accepted',
] as const;
export type OpportunityEmailEvent = (typeof OPPORTUNITY_EMAIL_EVENTS)[number];
export const EmailRecipientSchema = z.discriminatedUnion('kind', [
  /** the buyer's owners/admins (customer workspace, or the buying agency) and the person who submitted the requirements */
  z.object({ kind: z.literal('customer') }).strict(),
  /** a sales staff profile (address resolved server side from crm_sales_staff) */
  z.object({ kind: z.literal('staff'), userId: Id }).strict(),
  /** the seller's sales mailbox: Quixo (SALES_INBOX_EMAIL, else the support mailbox) or the agency's active owners and admins */
  z.object({ kind: z.literal('sales_inbox') }).strict(),
]);
export type EmailRecipient = z.infer<typeof EmailRecipientSchema>;
export const EmailJobPayloadSchema = z.object({
  event: z.enum(OPPORTUNITY_EMAIL_EVENTS),
  opportunityId: Id,
  versionId: Id.optional(),
  to: z.array(EmailRecipientSchema).min(1).max(10),
}).strict();
export type EmailJobPayload = z.infer<typeof EmailJobPayloadSchema>;
